import ExcelJS from 'exceljs';
import { formatBrandName } from './format';
import { getEffectivePrice } from './priceUtils';

/**
 * Fetch image through CORS proxy
 */
const fetchImageWithProxy = async (imageUrl) => {
  const proxies = [
    'https://corsproxy.io/?',
    'https://api.allorigins.win/raw?url=',
  ];
  
  for (const proxyUrl of proxies) {
    try {
      const response = await fetch(proxyUrl + encodeURIComponent(imageUrl), {
        headers: { 'Accept': 'image/*' }
      });
      if (response.ok) {
        return await response.blob();
      }
    } catch (error) {
      console.warn(`Proxy ${proxyUrl} failed, trying next...`);
    }
  }
  
  throw new Error('All proxies failed');
};

export const exportPropertySheetToExcel = async (rooms, propertyName = 'PropertySheet') => {
  const workbook = new ExcelJS.Workbook();
  
  for (const room of rooms) {
    if (!room?.items?.length) continue;
    
    const sheetName = room.name.replace(/[\\\\/:*?[\\]]/g, '').substring(0, 31);
    const worksheet = workbook.addWorksheet(sheetName);
    
    worksheet.columns = [
      { header: 'Image', key: 'image', width: 15 },
      { header: 'Qty', key: 'qty', width: 8 },
      { header: 'Product', key: 'product', width: 40 },
      { header: 'Brand price', key: 'price', width: 12 },
      { header: 'Adjusted price', key: 'adjustedPrice', width: 14 },
      { header: 'Price used', key: 'priceUsed', width: 12 },
      { header: 'Vendor', key: 'vendor', width: 15 },
      { header: 'Room', key: 'room', width: 15 },
      { header: 'Category', key: 'category', width: 15 },
      { header: 'Product Link', key: 'productLink', width: 30 },
      { header: 'Availability', key: 'availability', width: 12 },
      { header: 'Ordered', key: 'ordered', width: 10 }
    ];
    
    worksheet.getRow(1).font = { bold: true };
    
    for (let i = 0; i < room.items.length; i++) {
      const product = room.items[i];
      const rowIndex = i + 2;
      
      const effectivePrice = getEffectivePrice(product);
      const row = worksheet.addRow({
        image: '',
        qty: product?.quantity || 1,
        product: product?.title || '',
        price: product?.price ?? '',
        adjustedPrice: product?.adjustedPrice != null && product?.adjustedPrice !== '' ? product.adjustedPrice : '',
        priceUsed: effectivePrice,
        vendor: formatBrandName(product?.vendor) || '',
        room: room?.name || '',
        category: product?.category || '',
        productLink: product?.product_link || '',
        availability: product?.availability || '',
        ordered: product?.ordered ? 'Yes' : 'No'
      });
      
      row.height = 80;
      
      if (product?.image_link) {
        try {
          const blob = await fetchImageWithProxy(product.image_link);
          const arrayBuffer = await blob.arrayBuffer();
          
          let extension = 'jpeg';
          if (blob.type.includes('png')) extension = 'png';
          
          const imageId = workbook.addImage({
            buffer: arrayBuffer,
            extension: extension,
          });
          
          worksheet.addImage(imageId, {
            tl: { col: 0, row: rowIndex - 1 },
            ext: { width: 80, height: 80 },
            editAs: 'oneCell'
          });
        } catch (error) {
          console.error(`Failed to load image for ${product?.title}:`, error);
          worksheet.getCell(`A${rowIndex}`).value = 'Image failed';
        }
      }
    }
  }
  
  const timestamp = new Date().toISOString().split('T')[0];
  const filename = `${propertyName.replace(/[^a-zA-Z0-9]/g, '_')}_PropertySheet_${timestamp}.xlsx`;
  
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};